import { useNavigate } from 'react-router-dom';
import { useState, useContext } from 'react';
import Swal from 'sweetalert2';
import {
    Box,
    Button,
    TextField,
    Typography,
    Container,
    Avatar,
    Stack,
    Paper,
    CircularProgress
} from '@mui/material';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { UserContext } from '../context/UserContext';

const apiUrl = process.env.REACT_APP_API_URL;

export default function Login() {

    const navigate = useNavigate();

    const { setSession } = useContext(UserContext);

    const [user, setUser] = useState('');
    const [password, setPassword] = useState('');

    const [isLoading, setIsLoading] = useState(false);

    const handleLogin = async () => {
        if (!user || !password) {
            Swal.fire({
                title: 'Input Required ⚠️',
                text: 'Please fill out all required fields before proceeding.',
                icon: 'warning',
                confirmButtonText: 'Got it',
                confirmButtonColor: '#f0ad4e'
            });
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(`${apiUrl}login_user.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user: { user, password },
                }),
                credentials: "include",
            });

            const result = await response.json();

            if (result.status === 'success') {

                setSession({ logged_in: true, ...result, });

                navigate('/');

            } else if (result.status === 'error') {
                Swal.fire({ text: result.message, icon: 'info' });
            }
        } catch (error) {
            Swal.fire({ title: 'Server Error!', text: error, icon: 'error' });
            console.error('Server error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container
            component="main"
            maxWidth="xs"
            sx={{
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 400,
                    borderRadius: 1.3,
                    overflow: 'hidden',
                    border: '1px solid #2b2d30',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.24)',
                    backgroundColor: '#fff',
                }}
                component={Paper}
                elevation={0}
            >
                {/* Dark top banner */}
                <Box sx={{
                    px: 4, py: 3.5,
                    background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%)',
                    borderBottom: '1px solid #1E293B',
                }}>
                    <Box sx={{
                        width: 38, height: 38, borderRadius: '9px',
                        bgcolor: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mb: 2,
                    }}>
                        <LockOutlinedIcon sx={{ fontSize: 18, color: '#93C5FD' }} />
                    </Box>

                    <Typography sx={{
                        fontSize: 20, fontWeight: 800, color: '#F8FAFC',
                        letterSpacing: -0.5, lineHeight: 1.2,
                    }}>
                        Admin Portal
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748B', mt: 0.5 }}>
                        Restricted access — authorized personnel only
                    </Typography>
                </Box>

                {/* Form */}
                <Box sx={{ px: 4, py: 3.5 }}>
                    <Stack direction="column" spacing={2.25}>

                        <Box>
                            <Typography sx={{
                                fontSize: 11, fontWeight: 700, color: '#475569',
                                letterSpacing: 0.8, mb: 0.6, textTransform: 'uppercase',
                            }}>
                                Username
                            </Typography>
                            <TextField
                                required
                                fullWidth
                                id="user"
                                size="small"
                                autoFocus
                                autoComplete="off"
                                placeholder="Enter admin username"
                                value={user}
                                onChange={(e) => setUser(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        fontSize: 13,
                                        fontFamily: 'monospace',
                                        backgroundColor: '#F8FAFC',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#94A3B8' },
                                        '&.Mui-focused fieldset': { borderColor: '#0F172A', borderWidth: 1.5 },
                                    },
                                }}
                            />
                        </Box>

                        <Box>
                            <Typography sx={{
                                fontSize: 11, fontWeight: 700, color: '#475569',
                                letterSpacing: 0.8, mb: 0.6, textTransform: 'uppercase',
                            }}>
                                Password
                            </Typography>
                            <TextField
                                required
                                fullWidth
                                type="password"
                                id="password"
                                size="small"
                                autoComplete="off"
                                placeholder="Enter password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        fontSize: 13,
                                        fontFamily: 'monospace',
                                        backgroundColor: '#F8FAFC',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#94A3B8' },
                                        '&.Mui-focused fieldset': { borderColor: '#0F172A', borderWidth: 1.5 },
                                    },
                                }}
                            />
                        </Box>

                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleLogin}
                            disabled={isLoading}
                            sx={{
                                mt: 0.5,
                                py: 1.2,
                                borderRadius: '8px',
                                background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%)',
                                fontWeight: 700,
                                fontSize: 13,
                                letterSpacing: 0.4,
                                textTransform: 'none',
                                // boxShadow: '0 4px 14px rgba(15,23,42,0.35)',
                                border: '1px solid #1E3A5F',

                                '&:hover': {
                                    background: 'linear-gradient(135deg, #1E293B 0%, #1E40AF 100%)',
                                    boxShadow: '0 6px 20px rgba(15,23,42,0.45)',
                                },

                                '&:disabled': {
                                    background: '#E2E8F0',
                                    color: '#94A3B8',
                                    boxShadow: 'none',
                                    border: '1px solid #d4d5d6',
                                },
                            }}
                        >
                            {isLoading
                                ? <CircularProgress size={18} sx={{ color: '#1E3A5F' }} />
                                : 'Login'
                            }
                        </Button>

                    </Stack>

                    {/* Footer note */}
                    {/* <Box sx={{
                        mt: 3, pt: 2.5,
                        borderTop: '1px solid #F1F5F9',
                        display: 'flex', alignItems: 'center', gap: 0.75,
                    }}>
                        <Box sx={{
                            width: 6, height: 6, borderRadius: '50%',
                            bgcolor: '#22C55E',
                            boxShadow: '0 0 0 2px rgba(34,197,94,0.2)',
                        }} />
                        <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                            Secured connection &nbsp;·&nbsp; All activity is logged
                        </Typography>
                    </Box> */}
                </Box>
            </Box>

        </Container>
    );
}

